import React from 'react'
import {
  makeStyles,
  Grid,
  Typography,
  Chip,
  Link,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@material-ui/core'
import ArrowDropDownCircleIcon from '@material-ui/icons/ArrowDropDownCircle'
import ReactMarkdown from 'react-markdown'

const defaultFAQs = [
  {
    question: 'How do crypto-exchanges work?',
    answer:
      'Crypto exchanges are marketplaces that connect buyers and sellers. They provide an easy option for new users to buy crypto with conventional payment methods like credit card or bank transfer.',
  },
  {
    question: 'Can I store crypto on an exchange?',
    answer:
      'Yes, after you’ve bought crypto you can also leave it on the exchange for them to keep it under custody. This means you’re not in full control though. You can also transfer the crypto out of the exchange to any other wallet.',
  },
  {
    question: 'How are crypto exchanges regulated?',
    answer:
      'In most Western countries cryptocurrency exchanges need some sort of licenses to operate. In the US it is common for exchanges to register with FINCEN and apply for a money transmitter license while in Europe, exchanges frequently need a brokerage license. This is why users need to identify themselves upon registering with an exchange. Furthermore, they often need to have a crypto custody license.',
  },
  {
    question: 'How are crypto exchanges hacked?',
    answer:
      'Cryptocurrency exchanges hold user funds in two types of wallets: hot wallets and cold wallets. Hot wallets are connected to the internet so they are more exposed to hackers who can break into the servers of that exchange. That’s why most exchanges hold the majority of user funds in cold wallets, which are not connected to the internet. Often the keys to the cold wallets are split between multiple senior staff members of the exchange who all have to approve a transaction before funds can leave the exchange.',
  },
]

const useStyles = makeStyles((theme) => ({
  root: {
    background: '#211A29',
    padding: '80px 20px',

    margin: 'auto',
    [theme.breakpoints.up('sm')]: {
      padding: '80px 120px',
    },
  },
  header: {
    color: 'white',
    margin: '1rem 0',
    letterSpacing: '-0.02em',
  },
  chip: {
    background: 'rgba(11, 11, 11, 0.8)',
    color: 'white',
  },
  link: {
    color: 'white',
    fontSize: '.8rem',
    padding: '.4rem 0',
    borderBottom: '1px solid white',
  },
  contentSection: {
    margin: '1rem 0',
    '& p': {
      color: 'white',
    },
  },
  contentRow: {
    [theme.breakpoints.down('sm')]: {
      padding: '0 12px !important',
    },
  },
  contentCard: {
    padding: '1rem',
    background: 'rgba(255, 255, 255, 0.06)',
    borderRadius: '.4rem',
    height: 'fit-content',
    margin: '2rem 0',
    [theme.breakpoints.down('sm')]: {
      margin: '2rem 0 0',
    },
  },
  cardBody: {
    fontSize: '1rem',
  },
  cardHeader: {
    margin: '0 0 1rem 0',
    fontSize: '1.2rem',
    fontWeight: 'bold',
  },
  accordion: {
    background: 'transparent',
    border: 'unset',
    color: 'white',
  },
  expandedAccordion: {},
  accordionSummary: {
    padding: '0',
  },
  accordionDetails: {},
  downIcon: {},
  icon: {
    margin: '0 4px',
  },
  markdown: {
    fontSize: '18px',
    color: '#555555',
    lineHeight: '1.8em',
    overflowWrap: 'break-word',
    '& img': {
      maxWidth: '100%',
    },
    '& a': {
      color: theme.palette.primary.main,
      fontWeight: 'bold',
    },
  },
  reviewMarkdown: {
    color: '#585858',
  },
}))

interface FAQ {
  question: string
  answer: string
}
interface props {
  faqs?: FAQ[]
  title?: string
  subtitle?: string
  allQuestionsLink?: string
}

const defaultProps = {
  faqs: defaultFAQs,
  title: 'Frequently asked questions',
  subtitle: 'See all questions',
  allQuestionsLink: '#',
}

export default function FAQSection({
  faqs,
  title,
  subtitle,
  allQuestionsLink,
}: props): JSX.Element {
  const classes = useStyles({})
  const filterFAQs = (row: number) => {
    const filteredFaqs = []
    if (faqs) {
      for (let i = row - 1; i < faqs.length; i += 3) {
        filteredFaqs.push(faqs[i])
      }
    }
    return filteredFaqs
  }
  return (
    <div className={classes.root}>
      <Chip label="FAQ" className={classes.chip} />
      <Typography variant="h1" className={classes.header}>
        {title}
      </Typography>
      <Link className={classes.link} underline="none" href={allQuestionsLink}>
        {subtitle}
      </Link>
      <div>
        <Grid container spacing={3} className={classes.contentSection}>
          <Grid item xs={12} sm={6} md={4} className={classes.contentRow}>
            {filterFAQs(1).map((faq, index) => (
              <div key={index} className={classes.contentCard}>
                <Accordion
                  classes={{ root: classes.accordion, expanded: classes.expandedAccordion }}
                >
                  <AccordionSummary classes={{ root: classes.accordionSummary }}>
                    <ArrowDropDownCircleIcon className={classes.icon} />
                    <span className={classes.downIcon}>{faq.question}</span>
                  </AccordionSummary>
                  <AccordionDetails className={classes.accordionDetails}>
                    <ReactMarkdown className={`${classes.markdown} ${classes.reviewMarkdown}`}>
                      {faq.answer}
                    </ReactMarkdown>
                  </AccordionDetails>
                </Accordion>
              </div>
            ))}
          </Grid>
          <Grid item xs={12} sm={6} md={4} className={classes.contentRow}>
            {filterFAQs(2).map((faq, index) => (
              <div key={index} className={classes.contentCard}>
                <Accordion
                  classes={{ root: classes.accordion, expanded: classes.expandedAccordion }}
                >
                  <AccordionSummary classes={{ root: classes.accordionSummary }}>
                    <ArrowDropDownCircleIcon className={classes.icon} />
                    <span className={classes.downIcon}>{faq.question}</span>
                  </AccordionSummary>
                  <AccordionDetails className={classes.accordionDetails}>
                    <ReactMarkdown className={`${classes.markdown} ${classes.reviewMarkdown}`}>
                      {faq.answer}
                    </ReactMarkdown>
                  </AccordionDetails>
                </Accordion>
              </div>
            ))}
          </Grid>
          <Grid item xs={12} sm={6} md={4} className={classes.contentRow}>
            {filterFAQs(3).map((faq, index) => (
              <div key={index} className={classes.contentCard}>
                <Accordion
                  classes={{ root: classes.accordion, expanded: classes.expandedAccordion }}
                >
                  <AccordionSummary classes={{ root: classes.accordionSummary }}>
                    <ArrowDropDownCircleIcon className={classes.icon} />
                    <span className={classes.downIcon}>{faq.question}</span>
                  </AccordionSummary>
                  <AccordionDetails className={classes.accordionDetails}>
                    <ReactMarkdown className={`${classes.markdown} ${classes.reviewMarkdown}`}>
                      {faq.answer}
                    </ReactMarkdown>
                  </AccordionDetails>
                </Accordion>
              </div>
            ))}
          </Grid>
        </Grid>
      </div>
    </div>
  )
}

FAQSection.defaultProps = defaultProps
